import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNotify } from "../../api/Notify";
import toast from "react-hot-toast";

import {
    getAllChargingConnectorsSimplified,
    get_transaction_by_ConnectorPublicId,
    getCurrentReservationByConnectorPublicId,
} from "../../api/ChargingConnector";
import { useAuth, Roles } from "../../providers/AuthContext";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAllInternalCustomers } from "../../api/Customers";
import {
    getCustomerDisplayName,
    shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";
import { getAllCustomerTokens } from "../../api/Tokens";
import { getSessionDetailsByConnectors } from "../../api/ChargingSession";
import StartChargingModal from "../../components/chargeMonitor/StartChargingModal";
import ConfirmModal from "../../components/chargeMonitor/ConfirmModal";
import AvailabilityModal from "../../components/chargeMonitor/AvailabilityModal";
import LogConPointModal from "../../components/chargeMonitor/LogConPointModal";
import SiteOperationsMapModal from "../../components/chargeMonitor/SiteOperationsMapModal";
import ChargeMonitorConnectorCard from "../../components/chargeMonitor/ChargeMonitorConnectorCard";
import ChargeMonitorConnectorModal from "../../components/chargeMonitor/ChargeMonitorConnectorModal";
import {
    ChargeMonitorConnectorListRow,
    ChargeMonitorConnectorBox,
} from "../../components/chargeMonitor/ChargeMonitorCompact";
import { SESSION_STATUSES } from "../../components/chargeMonitor/chargeMonitorHelpers";
import SelectDropdown from "../../components/SelectDropdown";
import LoadingSpinner from "../../components/LoadingSpinner";


import {
    StatusIconByKey,
    getStatusLabel,
    getStatusChipStyleVars,
} from "../../helpers/StatusMapper";


import {
    post_RemoteManagement_remote_start_charging,
    post_RemoteManagement_remote_stop_charging,
    put_RemoteManagement_change_availability,
    post_RemoteManagement_reset,
    post_RemoteManagement_unlock_connector,
    delete_Reservation_cancel_reservation,
} from "../../api/Ocpp";
import {
    put_RemoteManagement_change_availability as put_RemoteManagement_change_availability_201,
    post_RemoteManagement_reset as post_RemoteManagement_reset_201,
    post_RemoteManagement_unlock_connector as post_RemoteManagement_unlock_connector_201,
    post_RemoteManagement_remote_start_charging as post_RemoteManagement_remote_start_charging_201,
    post_RemoteManagement_remote_stop_charging as post_RemoteManagement_remote_stop_charging_201,
    delete_Reservation_cancel_reservation as delete_Reservation_cancel_reservation_201,
} from "../../api/Ocpp201";
import { isOcpp201 } from "../../components/point/ocppPointHelpers";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faMagnifyingGlass,
    faPlugCircleBolt,
    faCircleCheck,
    faCircleXmark,
    faTableList,
    faList,
    faGrid2,
} from "@fortawesome/pro-regular-svg-icons";

const SESSION_DETAILS_DEBOUNCE_MS = 250;
const VIEW_MODE_STORAGE_KEY = "chargeMonitor.viewMode";
const VIEW_MODES = [
    { id: "cards", icon: faTableList },
    { id: "list", icon: faList },
    { id: "boxes", icon: faGrid2 },
];
const VIEW_MODE_IDS = VIEW_MODES.map((mode) => mode.id);

function readStoredViewMode() {
    try {
        const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        if (VIEW_MODE_IDS.includes(stored)) {
            return stored;
        }
    } catch {
        return "cards";
    }

    return "cards";
}

function getConnectorPublicId(data) {
    return data?.chargingConnectorPublicId || data?.ChargingConnectorPublicId;
}

function sessionFieldsFromDetails(details) {
    return {
        SessionCustomerName: details?.customerName ?? null,
        SessionTokenName: details?.tokenName ?? null,
        SessionStartTime: details?.sessionStartTimeUtc ?? null,
    };
}

export const ChargeMonitor = () => {
    const { t } = useTranslation();
    const [statusUpdates, setStatusUpdates] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const { getAccessToken, hasRole, hasAnyRole } = useAuth();


    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [tokens, setTokens] = useState([]);

    const [startModal, setStartModal] = useState({ isOpen: false, connectorId: null, ocppVersion: null });
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        message: "",
        extraInfo: {},
        onConfirm: () => { },
    });
    const [availabilityModal, setAvailabilityModal] = useState({ isOpen: false, connectorId: null, ocppVersion: null });
    const [logModal, setLogModal] = useState({
        isOpen: false,
        pointPublicId: null,
        connectorPublicId: null,
    });
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [viewMode, setViewMode] = useState(readStoredViewMode);
    const [detailConnectorId, setDetailConnectorId] = useState(null);
    const [expandedConnector, setExpandedConnector] = useState(null);
    const [renderedDetailsConnector, setRenderedDetailsConnector] = useState(null);
    const detailsCollapseTimerRef = useRef(null);
    const reservationFetchInFlightRef = useRef({});
    const reservationFetchAttemptedRef = useRef(new Set());
    const pendingSessionDetailIdsRef = useRef(new Set());
    const sessionDetailsTimerRef = useRef(null);
    const sessionDetailsFlushInFlightRef = useRef(false);

    const clearReservationFetchTracking = useCallback((connectorPublicId) => {
        delete reservationFetchInFlightRef.current[connectorPublicId];
        reservationFetchAttemptedRef.current.delete(connectorPublicId);
    }, []);

    const DETAILS_ANIMATION_MS = 800;

    const clearDetailsTimers = useCallback(() => {
        if (detailsCollapseTimerRef.current) {
            clearTimeout(detailsCollapseTimerRef.current);
            detailsCollapseTimerRef.current = null;
        }
    }, []);

    const closeDetails = useCallback(() => {
        clearDetailsTimers();
        setExpandedConnector(null);
        detailsCollapseTimerRef.current = setTimeout(() => {
            setRenderedDetailsConnector(null);
            detailsCollapseTimerRef.current = null;
        }, DETAILS_ANIMATION_MS + 20);
    }, [clearDetailsTimers]);

    const openDetails = useCallback((id) => {
        clearDetailsTimers();
        setRenderedDetailsConnector(id);
        setExpandedConnector(id);
    }, [clearDetailsTimers]);

    const toggleExpand = (id) => {
        if (expandedConnector === id) {
            closeDetails();
            return;
        }

        openDetails(id);
    };

    useEffect(() => () => {
        clearDetailsTimers();
        if (sessionDetailsTimerRef.current) {
            clearTimeout(sessionDetailsTimerRef.current);
            sessionDetailsTimerRef.current = null;
        }
    }, [clearDetailsTimers]);

    const fetchAndAttachSessionDetails = useCallback(async (connectorPublicIds) => {
        const ids = [...new Set((connectorPublicIds || []).filter(Boolean))];
        if (ids.length === 0) {
            return;
        }

        try {
            const token = await getAccessToken();
            const detailsList = await getSessionDetailsByConnectors(token, ids);
            if (!Array.isArray(detailsList) || detailsList.length === 0) {
                return;
            }

            const detailsByConnectorId = new Map();
            detailsList.forEach((details) => {
                const id = getConnectorPublicId(details);
                if (id) {
                    detailsByConnectorId.set(id, details);
                }
            });

            if (detailsByConnectorId.size === 0) {
                return;
            }

            setStatusUpdates(prev =>
                prev.map(item => {
                    const id = getConnectorPublicId(item.Data);
                    const details = detailsByConnectorId.get(id);
                    if (!details) {
                        return item;
                    }

                    const status = (item.Data.Status || item.Data.chargingConnectorStatus || "").toUpperCase();
                    if (!SESSION_STATUSES.includes(status)) {
                        return item;
                    }

                    return {
                        ...item,
                        Data: {
                            ...item.Data,
                            ...sessionFieldsFromDetails(details),
                        }
                    };
                })
            );
        } catch (err) {
            console.warn("Failed to fetch session details:", err);
        }
    }, [getAccessToken]);

    const flushPendingSessionDetails = useCallback(async () => {
        if (sessionDetailsFlushInFlightRef.current) {
            sessionDetailsTimerRef.current = setTimeout(() => {
                flushPendingSessionDetails();
            }, SESSION_DETAILS_DEBOUNCE_MS);
            return;
        }

        const ids = Array.from(pendingSessionDetailIdsRef.current);
        pendingSessionDetailIdsRef.current.clear();
        if (ids.length === 0) {
            return;
        }

        sessionDetailsFlushInFlightRef.current = true;
        try {
            await fetchAndAttachSessionDetails(ids);
        } finally {
            sessionDetailsFlushInFlightRef.current = false;
            if (pendingSessionDetailIdsRef.current.size > 0) {
                sessionDetailsTimerRef.current = setTimeout(() => {
                    flushPendingSessionDetails();
                }, SESSION_DETAILS_DEBOUNCE_MS);
            }
        }
    }, [fetchAndAttachSessionDetails]);

    const requestSessionDetails = useCallback((connectorPublicId) => {
        if (!connectorPublicId) {
            return;
        }

        pendingSessionDetailIdsRef.current.add(connectorPublicId);
        if (sessionDetailsTimerRef.current) {
            clearTimeout(sessionDetailsTimerRef.current);
        }
        sessionDetailsTimerRef.current = setTimeout(() => {
            sessionDetailsTimerRef.current = null;
            flushPendingSessionDetails();
        }, SESSION_DETAILS_DEBOUNCE_MS);
    }, [flushPendingSessionDetails]);

    const fetchAndAttachReservationDetails = useCallback(async (connectorPublicId) => {
        try {
            const token = await getAccessToken();
            const reservation = await getCurrentReservationByConnectorPublicId(token, connectorPublicId);

            setStatusUpdates(prev =>
                prev.map(item => {
                    const id =
                        item.Data.chargingConnectorPublicId ||
                        item.Data.ChargingConnectorPublicId;

                    if (id !== connectorPublicId) return item;

                    const status = (item?.Data?.Status || item?.Data?.chargingConnectorStatus || "").toUpperCase();
                    if (status !== "RESERVED") return item;

                    return {
                        ...item,
                        Data: {
                            ...item.Data,
                            CurrentReservationPublicId: reservation?.reservationPublicId ?? reservation?.ReservationPublicId ?? null,
                            CurrentReservationStartTime: reservation?.startDateTimeUtc ?? reservation?.StartDateTimeUtc ?? null,
                            CurrentReservationExpiryTime: reservation?.expiryDateTimeUtc ?? reservation?.ExpiryDateTimeUtc ?? null,
                        }
                    };
                })
            );
        } catch (err) {
            console.warn("Failed to fetch reservation details:", err);
        } finally {
            reservationFetchAttemptedRef.current.add(connectorPublicId);
            reservationFetchInFlightRef.current[connectorPublicId] = false;
        }
    }, [getAccessToken]);

    const requestReservationDetails = useCallback((connectorPublicId) => {
        if (!connectorPublicId) {
            return;
        }

        if (reservationFetchAttemptedRef.current.has(connectorPublicId)) {
            return;
        }

        if (reservationFetchInFlightRef.current[connectorPublicId]) {
            return;
        }

        reservationFetchInFlightRef.current[connectorPublicId] = true;
        fetchAndAttachReservationDetails(connectorPublicId);
    }, [fetchAndAttachReservationDetails]);

    const handleGetCustomers = useCallback(async () => {
        try {
            const token = await getAccessToken();
            const list = await getAllInternalCustomers(token);
            return Array.isArray(list)
                ? list.map((customer) => ({
                    ...customer,
                    customerName: getCustomerDisplayName(
                        customer,
                        shouldShowOrganizationName(hasRole)
                    ),
                }))
                : [];
        } catch (err) {
            console.error("Failed to fetch customers:", err);
            return [];
        }
    }, [getAccessToken,hasRole]);

    const handleGetTokens = async (customerPublicId) => {
        try {
            const token = await getAccessToken();
            const list = await getAllCustomerTokens(customerPublicId, token);
            setTokens(list || []);
        } catch (err) {
            console.error(err);
        }
    };

    const handleStartCharging = async (tagToken, connectorId, ocppVersion) => {
        const toastId = toast.loading(t("chargeMonitor.startingCharging"));

        try {
            const token = await getAccessToken();
            const payload = {
                tagToken,
                chargingConnectorPublicId: connectorId,
            };

            if (isOcpp201(ocppVersion)) {
                await post_RemoteManagement_remote_start_charging_201(token, payload);
            } else {
                await post_RemoteManagement_remote_start_charging(token, payload);
            }

            toast.success(t("chargeMonitor.chargingStarted"), { id: toastId });
        } catch (err) {
            console.error(err);

            const message =
                err instanceof Error ? err.message : t("chargeMonitor.failedStartCharging");

            toast.error(message, { id: toastId });
        }
    };

    const handleStopCharging = async (connectorPublicId, ocppVersion) => {
        const toastId = toast.loading(t("chargeMonitor.stoppingCharging"));

        try {
            const token = await getAccessToken();

            const transaction = await get_transaction_by_ConnectorPublicId(
                token,
                connectorPublicId
            );

            const payload = {
                transactionId: transaction.transactionPublicId,
            };

            if (isOcpp201(ocppVersion)) {
                await post_RemoteManagement_remote_stop_charging_201(token, payload);
            } else {
                await post_RemoteManagement_remote_stop_charging(token, payload);
            }

            toast.success(t("chargeMonitor.chargingStopped"), { id: toastId });
        } catch (err) {
            console.error(err);

            const message =
                err instanceof Error ? err.message : t("chargeMonitor.failedStopCharging");

            toast.error(message, { id: toastId });
        }
    };

    const handleChangeAvailability = async (connectorId, type, ocppVersion) => {
        const toastId = toast.loading(t("chargeMonitor.changingAvailability"));
        const requestType = type === "Inoperative" ? "Maintenance" : type;

        try {
            const token = await getAccessToken();
            const payload = {
                chargingConnectorPublicId: connectorId,
                type: requestType,
            };

            const response = isOcpp201(ocppVersion)
                ? await put_RemoteManagement_change_availability_201(token, payload)
                : await put_RemoteManagement_change_availability(token, payload);

            const status = response?.payload?.status;

            if (status === "Scheduled") {
                toast.success(t("chargeMonitor.statusChangedScheduled"), { id: toastId });
                return;
            }

            if (requestType === "Maintenance" || requestType === "Operative") {
                setStatusUpdates((prev) =>
                    prev.map((item) => {
                        const id = getConnectorPublicId(item.Data);
                        if (id !== connectorId) {
                            return item;
                        }

                        const nextData = {
                            ...item.Data,
                            maintenanceMode: requestType === "Maintenance",
                        };

                        if (requestType === "Maintenance") {
                            nextData.Status = "UNAVAILABLE";
                            nextData.chargingConnectorStatus = "UNAVAILABLE";
                        }

                        return {
                            ...item,
                            Data: nextData,
                        };
                    })
                );
            }

            toast.success(t("chargeMonitor.connectorSetTo", {
                type: t(`chargeMonitor.${String(type).toLowerCase()}`, { defaultValue: type }),
            }), { id: toastId });
        } catch (err) {
            console.error(err);

            const message =
                err instanceof Error ? err.message : t("chargeMonitor.failedChangeAvailability");

            toast.error(message, { id: toastId });
        } 
    };

    const handleRestart = async (chargingPointPublicId, ocppVersion) => {
        const toastId = toast.loading(t("toast.restartingCharger"));

        try {
            const token = await getAccessToken();
            const payload = {
                chargingPointPublicId,
                type: "Hard",
            };

            if (isOcpp201(ocppVersion)) {
                await post_RemoteManagement_reset_201(token, payload);
            } else {
                await post_RemoteManagement_reset(token, payload);
            }

            toast.success(t("toast.restartCommandSent"), { id: toastId });
        } catch (err) {
            console.error("❌ Restart failed:", err);

            const message =
                err instanceof Error ? err.message : t("toast.restartFailed");

            toast.error(message, { id: toastId });
        }
    };

    const handleUnlock = async (chargingConnectorPublicId, ocppVersion) => {
        const toastId = toast.loading(t("chargeMonitor.unlockingConnector"));

        try {
            const token = await getAccessToken();
            const payload = {
                chargingConnectorPublicId,
            };

            const res = isOcpp201(ocppVersion)
                ? await post_RemoteManagement_unlock_connector_201(token, payload)
                : await post_RemoteManagement_unlock_connector(token, payload);

            const status = res?.payload?.status ?? res?.Status ?? "Unknown";

            if (status === "Accepted" || status === "Unlocked") {
                toast.success(t("chargeMonitor.connectorUnlocked"), { id: toastId });
            } else if (status === "NotSupported") {
                toast.error(t("chargeMonitor.unlockNotSupported"), { id: toastId });
            } else {
                toast(t("chargeMonitor.unlockResponse", { status }), { id: toastId, icon: "ℹ️" });
            }
        } catch (err) {
            console.error(err);

            const message =
                err instanceof Error ? err.message : t("chargeMonitor.failedUnlock");

            toast.error(message, { id: toastId });
        }
    };

    const handleCancelReservation = async (reservationPublicId, connectorPublicId, ocppVersion) => {
        const toastId = toast.loading(t("chargeMonitor.cancelingReservation"));

        try {
            const token = await getAccessToken();
            if (isOcpp201(ocppVersion)) {
                await delete_Reservation_cancel_reservation_201(token, reservationPublicId);
            } else {
                await delete_Reservation_cancel_reservation(token, reservationPublicId);
            }

            toast.success(t("chargeMonitor.reservationCanceled"), { id: toastId });

            setStatusUpdates(prev =>
                prev.map(item => {
                    const id =
                        item.Data.chargingConnectorPublicId ||
                        item.Data.ChargingConnectorPublicId;

                    if (id !== connectorPublicId) return item;

                    return {
                        ...item,
                        Data: {
                            ...item.Data,
                            CurrentReservationPublicId: null,
                            CurrentReservationStartTime: null,
                            CurrentReservationExpiryTime: null,
                        }
                    };
                })
            );
            clearReservationFetchTracking(connectorPublicId);
        } catch (err) {
            console.error(err);
            const message =
                err instanceof Error ? err.message : t("chargeMonitor.failedCancelReservation");
            toast.error(message, { id: toastId });
        }
    };


    useEffect(() => {
        (async () => {
            try {
                const accessToken = await getAccessToken();
                const connectors = await getAllChargingConnectorsSimplified(accessToken);

                if (!Array.isArray(connectors)) {
                    return;
                }

                const connectorsWithIds = connectors.map((c) => ({
                    id: c.chargingConnectorPublicId || c.ChargingConnectorPublicId,
                    status: (c.chargingConnectorStatus || c.ChargingConnectorStatus || "").toUpperCase(),
                }));

                const sessionConnectorIds = connectorsWithIds
                    .filter((c) => c.id && SESSION_STATUSES.includes(c.status))
                    .map((c) => c.id);

                const reservedConnectorIds = connectorsWithIds
                    .filter((c) => c.id && c.status === "RESERVED")
                    .map((c) => c.id);

                const [detailsList, reservationResults] = await Promise.all([
                    sessionConnectorIds.length > 0
                        ? getSessionDetailsByConnectors(accessToken, sessionConnectorIds).catch((err) => {
                            console.warn("Could not fetch session details:", err);
                            return [];
                        })
                        : Promise.resolve([]),
                    reservedConnectorIds.length > 0
                        ? Promise.all(
                            reservedConnectorIds.map(async (connectorPublicId) => {
                                try {
                                    const reservation = await getCurrentReservationByConnectorPublicId(
                                        accessToken,
                                        connectorPublicId
                                    );
                                    return { connectorPublicId, reservation };
                                } catch (err) {
                                    console.warn("Could not fetch reservation:", err);
                                    return { connectorPublicId, reservation: null };
                                } finally {
                                    reservationFetchAttemptedRef.current.add(connectorPublicId);
                                }
                            })
                        )
                        : Promise.resolve([]),
                ]);

                const sessionByConnector = new Map();
                (detailsList || []).forEach((details) => {
                    const id = getConnectorPublicId(details);
                    if (id) {
                        sessionByConnector.set(id, sessionFieldsFromDetails(details));
                    }
                });

                const reservationByConnector = new Map();
                (reservationResults || []).forEach(({ connectorPublicId, reservation }) => {
                    reservationByConnector.set(connectorPublicId, {
                        CurrentReservationPublicId:
                            reservation?.reservationPublicId ?? reservation?.ReservationPublicId ?? null,
                        CurrentReservationStartTime:
                            reservation?.startDateTimeUtc ?? reservation?.StartDateTimeUtc ?? null,
                        CurrentReservationExpiryTime:
                            reservation?.expiryDateTimeUtc ?? reservation?.ExpiryDateTimeUtc ?? null,
                    });
                });

                const enriched = connectors.map((c) => {
                    const chargingPointPublicId = c.chargingPointPublicId || c.ChargingPointPublicId;
                    const chargingConnectorPublicId = c.chargingConnectorPublicId || c.ChargingConnectorPublicId;
                    const chargingSiteName = c.chargingSiteName || c.ChargingSiteName || "–";
                    const chargingPointName = c.chargingPointName || c.ChargingPointName || "–";
                    const chargingConnectorName = c.chargingConnectorName || c.ChargingConnectorName || "–";
                    const chargingSitePublicId = c.chargingSitePublicId || c.ChargingSitePublicId || "";
                    const latitude = c.latitude ?? c.Latitude;
                    const longitude = c.longitude ?? c.Longitude;
                    const maxElectricPower = c.maxElectricPower ?? c.MaxElectricPower;
                    const status = (c.chargingConnectorStatus || c.ChargingConnectorStatus || "").toUpperCase();
                    const isOnline = c.isOnline ?? c.IsOnline ?? false;
                    const maintenanceMode = !!(c.maintenanceMode ?? c.MaintenanceMode);

                    return {
                        Data: {
                            ...c,
                            chargingPointPublicId,
                            chargingConnectorPublicId,
                            ChargingSiteName: chargingSiteName,
                            chargingSitePublicId,
                            chargingPointName,
                            chargingConnectorName,
                            latitude,
                            longitude,
                            maxElectricPower,
                            isOnline,
                            maintenanceMode,
                            Status: status,
                            ...(sessionByConnector.get(chargingConnectorPublicId) || {}),
                            ...(reservationByConnector.get(chargingConnectorPublicId) || {}),
                        },
                        _justUpdated: false,
                    };
                });

                setStatusUpdates(enriched);
            } catch (err) {
                console.error("Error loading data:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [getAccessToken]);


    const statusUpdatesRef = useRef([]);
    useEffect(() => {
        statusUpdatesRef.current = statusUpdates;
    }, [statusUpdates]);

    const { connected } = useNotify(["status", "chargingpoint-status"], (msg) => {
        try {
            const parsed = typeof msg.data === "string" ? JSON.parse(msg.data) : msg.data;
            const data = parsed?.Data || parsed?.data || {};
            const type = parsed?.Metadata?.MessageType ?? msg.event ?? "";
            const normalizedType = (type || "").toLowerCase();

            if (normalizedType === "chargingpointstatus") {
                const chargingPointId = data?.ChargingPointPublicId || data?.chargingPointPublicId;
                const isOnline = !!(data?.IsOnline ?? data?.isOnline);
                if (!chargingPointId) return;

                setStatusUpdates((prev) =>
                    prev.map((x) => {
                        const match =
                            x.Data.chargingPointPublicId === chargingPointId ||
                            x.Data.ChargingPointPublicId === chargingPointId;

                        if (!match) return x;

                        return {
                            ...x,
                            Data: {
                                ...x.Data,
                                isOnline,
                                Status: isOnline ? x.Data.Status : "UNAVAILABLE",
                                chargingConnectorStatus: isOnline ? x.Data.chargingConnectorStatus : "UNAVAILABLE",
                            },
                        };
                    })
                );

                return;
            }



            if (["status"].includes(normalizedType)) {
                const connectorId = getConnectorPublicId(data);
                const newStatus =
                    data?.Status || data?.status || data?.ChargingConnectorStatus || data?.chargingConnectorStatus;

                if (!connectorId || !newStatus) return;

                const existing = statusUpdatesRef.current.find(
                    x => getConnectorPublicId(x.Data) === connectorId
                );
                if (!existing) {
                    return;
                }

                const upperStatus = newStatus.toUpperCase();

                if (SESSION_STATUSES.includes(upperStatus)) {
                    const previousStatus = (existing.Data.Status || existing.Data.chargingConnectorStatus || "").toUpperCase();
                    const enteredCharging = upperStatus === "CHARGING" && previousStatus !== "CHARGING";
                    const hasCustomer = existing.Data.SessionCustomerName;
                    const hasSession =
                        hasCustomer ||
                        existing.Data.SessionTokenName ||
                        existing.Data.SessionStartTime;

                    if (!hasSession || (enteredCharging && !hasCustomer)) {
                        requestSessionDetails(connectorId);
                    }
                }

                if (upperStatus === "RESERVED") {
                    const hasReservation =
                        existing.Data.CurrentReservationPublicId ||
                        existing.Data.CurrentReservationExpiryTime;

                    if (!hasReservation) {
                        requestReservationDetails(connectorId);
                    }
                } else {
                    clearReservationFetchTracking(connectorId);
                }

                setStatusUpdates((prev) =>
                    prev.map((x) => {
                        const match =
                            x.Data.ChargingConnectorPublicId === connectorId ||
                            x.Data.chargingConnectorPublicId === connectorId;

                        if (!match) return x;

                        const shouldKeepSession = SESSION_STATUSES.includes(upperStatus);
                        const shouldKeepReservation = upperStatus === "RESERVED";

                        return {
                            ...x,
                            Data: {
                                ...x.Data,
                                Status: upperStatus,
                                chargingConnectorStatus: upperStatus,
                                isOnline: true,

                                SessionCustomerName: shouldKeepSession ? x.Data.SessionCustomerName : null,
                                SessionTokenName: shouldKeepSession ? x.Data.SessionTokenName : null,
                                SessionStartTime: shouldKeepSession ? x.Data.SessionStartTime : null,

                                CurrentReservationPublicId: shouldKeepReservation ? x.Data.CurrentReservationPublicId : null,
                                CurrentReservationStartTime: shouldKeepReservation ? x.Data.CurrentReservationStartTime : null,
                                CurrentReservationExpiryTime: shouldKeepReservation ? x.Data.CurrentReservationExpiryTime : null,
                            },
                            _justUpdated: true,
                        };
                    })
                );

                setTimeout(() => {
                    setStatusUpdates((cur) =>
                        cur.map((x) =>
                            x.Data.ChargingConnectorPublicId === connectorId ||
                                x.Data.chargingConnectorPublicId === connectorId
                                ? { ...x, _justUpdated: false }
                                : x
                        )
                    );
                }, 1500);
            }

        } catch (err) {
            console.warn("⚠️ Invalid SSE message:", msg, err);
        }
    });

    useEffect(() => {
        if (!expandedConnector) return;

        const current = statusUpdates.find(
            x =>
                x.Data.chargingConnectorPublicId === expandedConnector ||
                x.Data.ChargingConnectorPublicId === expandedConnector
        );

        const status = (current?.Data?.Status || current?.Data?.chargingConnectorStatus || "").toUpperCase();

        if (status && !SESSION_STATUSES.includes(status)) {
            closeDetails();
        }
    }, [statusUpdates, expandedConnector, closeDetails]);

    const hiddenStatuses = ["SUSPENDEDEV", "SUSPENDEDEVSE", "EVCONNECTED", "IDLE", "UNAVAILABLE", "INOPERATIVE", "PENDING", "RESERVED"];
    const statuses = [
        "AVAILABLE",
        "OCCUPIED",
        "PREPARING",
        "CHARGING",
        "IDLE",
        "FINISHING",
        "INOPERATIVE",
        "UNAVAILABLE",
        "RESERVED",
        "PENDING",
        "FAULTED",
        "SUSPENDEDEV",
        "OFFLINE",
    ];

    const allStatuses = [
        ...statuses.filter(s => !hiddenStatuses.includes(s)),
        "OTHER"
    ];
    const toggleStatus = (status) => {
        setSelectedStatuses((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
    };

    const defaultOrder = ["OCCUPIED", "PREPARING", "CHARGING", "EVCONNECTED", "IDLE", "FINISHING", "SUSPENDEDEV", "SUSPENDEDEVSE", "RESERVED", "AVAILABLE", "UNAVAILABLE"];

    const filtered = statusUpdates
        .filter((item) => {
            const d = item.Data;
            const search = filterText.toLowerCase();

            const matchesText =
                (d.ChargingSiteName || "").toLowerCase().includes(search) ||
                (d.chargingPointName || "").toLowerCase().includes(search) ||
                (d.chargingConnectorName || "").toLowerCase().includes(search) ||
                (d.chargingConnectorStatus || d.Status || "").toLowerCase().includes(search);

            const status = (d.Status || d.chargingConnectorStatus || "").toUpperCase();
            const isHiddenStatus = hiddenStatuses.includes(status);

            const isOnline = !!d.isOnline;
            const isOffline = !isOnline;

            // OFFLINE = modifier (AND) när kombinerad med andra
            const wantsOffline = selectedStatuses.includes("OFFLINE");
            const otherSelected = selectedStatuses.filter((s) => s !== "OFFLINE");

            const matchesOtherStatuses =
                otherSelected.length === 0 ||
                otherSelected.includes(status) ||
                (otherSelected.includes("OTHER") && isHiddenStatus);

            const matchesStatus =
                matchesOtherStatuses && (!wantsOffline || isOffline);


            return matchesText && matchesStatus;
        })

        .sort((a, b) => {
            const getConnectorStatus = (item) => {
                const d = item.Data || {};
                return (d.Status || d.chargingConnectorStatus || "").toUpperCase() || "UNKNOWN";
            };

            const isPointOnline = (item) => {
                const d = item.Data || {};
                return !!d.isOnline;
            };

            const sa = getConnectorStatus(a);
            const sb = getConnectorStatus(b);

            const ia = defaultOrder.indexOf(sa);
            const ib = defaultOrder.indexOf(sb);

            const rankA = ia === -1 ? 999 : ia;
            const rankB = ib === -1 ? 999 : ib;

            // 1) Alltid sortera på connector status
            if (rankA !== rankB) return rankA - rankB;

            // 2) Valfritt: inom samma status – online först (OFFLINE påverkar inte status-rank)
            const oa = isPointOnline(a);
            const ob = isPointOnline(b);
            if (oa !== ob) return oa ? -1 : 1;

            // 3) Stabil ordning inom samma status
            const siteA = (a.Data?.ChargingSiteName || "").toLowerCase();
            const siteB = (b.Data?.ChargingSiteName || "").toLowerCase();
            if (siteA !== siteB) return siteA.localeCompare(siteB);

            const cpA = (a.Data?.chargingPointName || "").toLowerCase();
            const cpB = (b.Data?.chargingPointName || "").toLowerCase();
            if (cpA !== cpB) return cpA.localeCompare(cpB);

            const cnA = (a.Data?.chargingConnectorName || "").toLowerCase();
            const cnB = (b.Data?.chargingConnectorName || "").toLowerCase();
            return cnA.localeCompare(cnB);
        });



    // --- Count connectors per status (for chips) ---
    const statusCounts = statusUpdates.reduce((acc, item) => {
        const d = item.Data || {};
        const status = (d.Status || d.chargingConnectorStatus || "").toUpperCase() || "UNKNOWN";

        const isOnline = !!d.isOnline;

        if (!isOnline) acc.OFFLINE = (acc.OFFLINE || 0) + 1;

        if (hiddenStatuses.includes(status)) {
            acc.OTHER = (acc.OTHER || 0) + 1;
        } else {
            acc[status] = (acc[status] || 0) + 1;
        }

        return acc;
    }, {});


    const detailItem =
        statusUpdates.find((item) => getConnectorPublicId(item.Data) === detailConnectorId) || null;

    const canManage = hasAnyRole([Roles.Admin, Roles.CpmsLight, Roles.CustomerService, Roles.ServicePartner]);

    const handleConnectorAction = (actionId, item) => {
        const data = item?.Data || {};
        const extraInfo = {
            Site: data.ChargingSiteName,
            Charger: data.chargingPointName,
            Connector: data.chargingConnectorName,
        };
        const ocppVersion = data.ocppVersion || data.OcppVersion;

        if (actionId === "datalog") {
            setLogModal({
                isOpen: true,
                pointPublicId: data.chargingPointPublicId,
                connectorPublicId: data.chargingConnectorPublicId,
            });
            return;
        }

        if (actionId === "start") {
            setSelectedCustomer(null);
            setTokens([]);
            setStartModal({
                isOpen: true,
                connectorId: data.chargingConnectorPublicId,
                siteName: data.ChargingSiteName,
                chargerName: data.chargingPointName,
                connectorName: data.chargingConnectorName,
                ocppVersion,
            });
            return;
        }

        if (actionId === "stop") {
            setConfirmModal({
                isOpen: true,
                title: t("chargeMonitor.stopCharging"),
                message: t("chargeMonitor.stopChargingConfirm"),
                confirmLabel: t("chargeMonitor.stopCharging"),
                extraInfo,
                onConfirm: () => handleStopCharging(data.chargingConnectorPublicId, ocppVersion),
            });
            return;
        }

        if (actionId === "availability") {
            setAvailabilityModal({
                isOpen: true,
                connectorId: data.chargingConnectorPublicId,
                siteName: data.ChargingSiteName,
                chargerName: data.chargingPointName,
                connectorName: data.chargingConnectorName,
                ocppVersion,
            });
            return;
        }

        if (actionId === "restart") {
            setConfirmModal({
                isOpen: true,
                title: t("chargeMonitor.restartCharger"),
                message: t("chargeMonitor.restartChargerConfirm"),
                confirmLabel: t("common.restart"),
                extraInfo: {
                    Site: data.ChargingSiteName,
                    Charger: data.chargingPointName,
                },
                onConfirm: () => handleRestart(data.chargingPointPublicId, ocppVersion),
            });
            return;
        }

        if (actionId === "unlock") {
            setConfirmModal({
                isOpen: true,
                title: t("chargeMonitor.unlockConnector"),
                message: t("chargeMonitor.unlockConnectorConfirm"),
                confirmLabel: t("common.unlock"),
                extraInfo,
                onConfirm: () => handleUnlock(data.chargingConnectorPublicId, ocppVersion),
            });
            return;
        }

        if (actionId === "cancelReservation") {
            setConfirmModal({
                isOpen: true,
                title: t("chargeMonitor.cancelReservation"),
                message: t("chargeMonitor.cancelReservationMessage"),
                confirmLabel: t("chargeMonitor.cancelReservation"),
                extraInfo,
                onConfirm: () => handleCancelReservation(
                    data.CurrentReservationPublicId,
                    data.chargingConnectorPublicId,
                    ocppVersion
                ),
            });
            return;
        }

        if (actionId === "details") {
            toggleExpand(data.chargingConnectorPublicId);
        }
    };

    const changeViewMode = (mode) => {
        setViewMode(mode);
        try {
            localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
        } catch {
            // Ignore storage errors (private mode, quota, etc.)
        }
        if (mode === "cards") {
            setDetailConnectorId(null);
            return;
        }
        closeDetails();
    };

    return (
        <>
            <div
                className="flex flex-col overflow-hidden bg-mute-25"
                style={{ position: "fixed", top: 0, right: 0, bottom: 0, left: 0 }}
            >
                <HeaderBar />
                <div className="relative min-h-0 flex-1 overflow-hidden">
                    <Sidebar />
                    <main className="absolute inset-0 flex flex-col overflow-hidden md:left-[15rem]">
                        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-3 pt-3 md:gap-4 md:px-6 md:pt-6">
                            <div className="flex shrink-0 flex-col gap-3 md:gap-4">

                                {/* Icon + live/disconnected */}
                                <div className="flex items-center gap-2 text-xl font-semibold">
                                    <button
                                        type="button"
                                        className={`inline-flex items-center justify-center ${hasRole(Roles.Admin) ? "" : "pointer-events-none"}`}
                                        tabIndex={hasRole(Roles.Admin) ? 0 : -1}
                                        aria-disabled={!hasRole(Roles.Admin)}
                                        title={hasRole(Roles.Admin) ? t("common.map") : undefined}
                                        onClick={() => {
                                            if (!hasRole(Roles.Admin)) {
                                                return;
                                            }
                                            setIsMapOpen(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlugCircleBolt} className="text-sky-500 text-2xl" />
                                    </button>
                                    <span className={`text-sm font-medium ${connected ? "text-secondary-600" : "text-error-600"}`}>
                                        {connected ? t("chargeMonitor.liveFeed") : t("chargeMonitor.disconnected")}
                                    </span>
                                </div>

                                {/* Search under */}
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center bg-white border border-mute-200 rounded-xl shadow-md px-3 py-2 w-full md:w-96">
                                        <FontAwesomeIcon icon={faMagnifyingGlass} className="text-mute-400 mr-2" />
                                        <input
                                            type="text"
                                            className="w-full bg-transparent focus:outline-none text-sm text-mute-700"
                                            placeholder={t("chargeMonitor.searchPlaceholder")}
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                    </div>
                                    <div className="inline-flex rounded-xl border border-mute-200 bg-white p-1 shadow-md">
                                        {VIEW_MODES.map((mode) => (
                                            <button
                                                key={mode.id}
                                                type="button"
                                                className={`inline-flex size-8 items-center justify-center rounded-lg ${
                                                    viewMode === mode.id
                                                        ? "bg-mute-100"
                                                        : "text-mute-500 hover:bg-mute-50"
                                                }`}
                                                title={t(`chargeMonitor.view.${mode.id}`)}
                                                aria-label={t(`chargeMonitor.view.${mode.id}`)}
                                                onClick={() => changeViewMode(mode.id)}
                                            >
                                                <FontAwesomeIcon icon={mode.icon} />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                            </div>



                            {/* === Status-filter (desktop, statiskt) === */}
                            <div className="mb-0 hidden shrink-0 md:flex md:flex-wrap md:gap-2">
                                {allStatuses.map((s) => {
                                    const selected = selectedStatuses.includes(s);
                                    const icon =
                                        s === "OFFLINE" ? faCircleXmark : (StatusIconByKey[s] ?? StatusIconByKey.DEFAULT);

                                    const label = getStatusLabel(s, t);

                                    return (
                                        <button
                                            key={s}
                                            onClick={() => toggleStatus(s)}
                                            className={`chip ${selected ? "chip--active" : ""} shadow-md`}
                                            style={getStatusChipStyleVars(s)}
                                            type="button"
                                        >
                                            <FontAwesomeIcon icon={faCircleCheck} className="chip__check text-xs" />
                                            <FontAwesomeIcon
                                                icon={icon}
                                                className="text-xs"
                                            />
                                            <span>
                                                {label}
                                            </span>

                                            <span className="chip__count">
                                                {statusCounts[s] ?? 0}
                                            </span>

                                        </button>
                                    );
                                })}
                            </div>

                            {/* === LISTA === */}
                            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-3 pr-2 md:gap-4">
                                <div className="block shrink-0 md:hidden">
                                    <SelectDropdown
                                        options={allStatuses.map((s) => ({
                                            value: s,
                                            label: getStatusLabel(s, t),
                                        }))}
                                        selectedValues={selectedStatuses.map((s) => ({
                                            value: s,
                                            label: getStatusLabel(s, t),
                                        }))}
                                        onChange={(selected) => {
                                            if (!selected || selected.length === 0) {
                                                setSelectedStatuses([]);
                                            } else {
                                                setSelectedStatuses(selected.map((opt) => opt.value));
                                            }
                                        }}
                                        placeholder={t("chargeMonitor.selectStatus")}
                                        isMulti={true}
                                        isClearable={true}
                                        formatOptionLabel={({ value, label }) => (
                                            <div className="flex items-center gap-2">
                                                <FontAwesomeIcon
                                                    icon={StatusIconByKey[value] ?? StatusIconByKey.DEFAULT}
                                                    className="text-mute-600"
                                                />
                                                <span>{label}</span>
                                            </div>
                                        )}
                                    />
                                </div>
                            <div
                                className={
                                    viewMode === "boxes"
                                        ? "min-w-0 grid auto-rows-min grid-cols-2 items-stretch gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
                                        : viewMode === "list"
                                            ? "space-y-1"
                                            : "space-y-3"
                                }
                            >
                                {loading ? (
                                    <div className="col-span-full">
                                        <LoadingSpinner text={t("chargeMonitor.loadingChargers")} />
                                    </div>
                                ) : filtered.length === 0 ? (
                                    <p className="col-span-full text-mute-400 text-center py-6">{t("chargeMonitor.noMatchingChargers")}</p>
                                ) : (
                                    filtered.map((item) => {
                                        const key = getConnectorPublicId(item.Data);

                                        if (viewMode === "list") {
                                            return (
                                                <ChargeMonitorConnectorListRow
                                                    key={key}
                                                    item={item}
                                                    onOpen={() => setDetailConnectorId(key)}
                                                />
                                            );
                                        }

                                        if (viewMode === "boxes") {
                                            return (
                                                <ChargeMonitorConnectorBox
                                                    key={key}
                                                    item={item}
                                                    onOpen={() => setDetailConnectorId(key)}
                                                />
                                            );
                                        }

                                        return (
                                            <ChargeMonitorConnectorCard
                                                key={key}
                                                item={item}
                                                canManage={canManage}
                                                onAction={handleConnectorAction}
                                                showDetailsToggle
                                                detailsExpanded={expandedConnector === key}
                                                renderedDetails={renderedDetailsConnector === key}
                                            />
                                        );
                                    })
                                )}
                            </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            <ChargeMonitorConnectorModal
                item={detailItem}
                isOpen={viewMode !== "cards" && !!detailItem}
                onClose={() => setDetailConnectorId(null)}
                canManage={canManage}
                onAction={handleConnectorAction}
            />

            {/* Modaler */}
            <StartChargingModal
                isOpen={startModal.isOpen}
                setIsOpen={(v) => setStartModal((s) => ({ ...s, isOpen: v }))}
                connectorId={startModal.connectorId}
                siteName={startModal.siteName}
                chargerName={startModal.chargerName}
                connectorName={startModal.connectorName}
                handleGetCustomers={handleGetCustomers}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
                handleGetTokens={handleGetTokens}
                existingTokens={tokens}
                onStartCharging={(tagToken) =>
                    handleStartCharging(tagToken, startModal.connectorId, startModal.ocppVersion)
                }
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal((s) => ({ ...s, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                extraInfo={confirmModal.extraInfo}
                confirmLabel={confirmModal.confirmLabel}
            />

            <AvailabilityModal
                isOpen={availabilityModal.isOpen}
                setIsOpen={(v) => setAvailabilityModal((s) => ({ ...s, isOpen: v }))}
                connectorName={availabilityModal.connectorName}
                chargerName={availabilityModal.chargerName}
                siteName={availabilityModal.siteName}
                onSubmit={(type) =>
                    handleChangeAvailability(availabilityModal.connectorId, type, availabilityModal.ocppVersion)
                }
            />

            <LogConPointModal
                isOpen={logModal.isOpen}
                pointPublicId={logModal.pointPublicId}
                connectorPublicId={logModal.connectorPublicId}
                onClose={() =>
                    setLogModal({
                        isOpen: false,
                        pointPublicId: null,
                        connectorPublicId: null,
                    })
                }
            />

            {hasRole(Roles.Admin) && (
                <SiteOperationsMapModal
                    isOpen={isMapOpen}
                    onClose={() => setIsMapOpen(false)}
                    statusUpdates={statusUpdates}
                />
            )}
        </>
    );
}
